import type { OrderbookMessage } from "../hooks/useOrderbookStream";
import type { SeasonStanding } from "../types/market";

type TradeFill = OrderbookMessage["trades"][number];

function compareTradeFill(a: TradeFill, b: TradeFill) {
  const timeDiff = Date.parse(a.executed_at) - Date.parse(b.executed_at);
  if (timeDiff !== 0) return timeDiff;
  return a.trade_id - b.trade_id;
}

function getNewestFill(message: OrderbookMessage): TradeFill | null {
  if (!message.trades.length) return null;
  const sortedTrades = [...message.trades].sort(compareTradeFill);
  return sortedTrades[sortedTrades.length - 1] ?? null;
}

function getTrend(
  lastTradePrice: number,
  previousTradePrice: number | null,
): SeasonStanding["last_trade_trend"] {
  if (previousTradePrice == null) return null;
  if (lastTradePrice > previousTradePrice) return "UP";
  if (lastTradePrice < previousTradePrice) return "DOWN";
  return "FLAT";
}

export function applyMarketLastTradeToStandings(
  standings: SeasonStanding[],
  message: OrderbookMessage,
): SeasonStanding[] {
  const newestFill = getNewestFill(message);
  if (!newestFill) return standings;

  return standings.map((team) => {
    if (team.season_team_id !== message.season_team_id) {
      return team;
    }

    const previousTradePrice = team.last_trade_price;
    return {
      ...team,
      last_trade_price: newestFill.price,
      last_trade_quantity: newestFill.quantity,
      last_trade_executed_at: newestFill.executed_at,
      previous_trade_price: previousTradePrice,
      last_trade_trend: getTrend(newestFill.price, previousTradePrice),
    };
  });
}
